import * as _ from "lodash";
import { ElementType, IElement, IElementDictionary } from "../model/IProcessData";
import { CompilationGroupKey, ProcessGroupKey, ValidationError, ValidationErrorType } from "../model/ValidationData";
import { USE_STATEMENT_MODE } from "processEditor/model/ElementFactory";

export function hasValidationError(validation: ValidationError[] | undefined, validationErrorType: ValidationErrorType): boolean {
    const error = _.find(validation, { messageType: validationErrorType });
    return error !== undefined;
}

const NON_ELEMENT_HEADINGS: { [key: string]: string } = {
    [ProcessGroupKey]: "Process",
    processVariables: "Process variables",
    globalErrorHandler: "Global error handler",
    [CompilationGroupKey]: "Compilation errors"
};

/**
 * Whether a validation dictionary key is a shape on the canvas, and so can be highlighted and selected.
 * The process-level keys never are, and neither is an id whose shape has since been removed.
 */
export function isShapeValidationGroup(key: string, elements: IElementDictionary): boolean {
    return !NON_ELEMENT_HEADINGS[key] && !!elements[key];
}

/**
 * Heading for one group of validation errors. The dictionary is keyed by element id apart from a few
 * process-level keys, which are checked first because "globalErrorHandler" is also a real element id.
 * An element name is optional (missing one is itself a validation error), so the type label stands in,
 * and an id with no element left in the diagram falls back to the raw id rather than disappearing.
 */
export function getValidationGroupHeading(key: string, elements: IElementDictionary): string {
    if (NON_ELEMENT_HEADINGS[key]) {
        return NON_ELEMENT_HEADINGS[key];
    }

    const element = elements[key];
    if (!element) {
        return key;
    }

    return element.name || getElementName(element.type);
}

export function getElementName(type: ElementType): string {
    switch (type) {
        case ElementType.CallActivity:
            return "Call Subprocess";
        case ElementType.Catch:
            return "Catch";
        case ElementType.ConditionBranch:
            return "Exclusive Condition Branch";
        case ElementType.Decision:
            return "Exclusive Decision";
        case ElementType.Expression:
            return "Code";
        case ElementType.InclusiveDecisionBranch:
            return "Inclusive Condition Branch";
        case ElementType.InclusiveGateway:
            return "Inclusive Decision";
        case ElementType.IntermediateReturn:
            return "Intermediate Return";
        case ElementType.ParallelForeach:
        case ElementType.SequentialForeach:
            return "Foreach";
        case ElementType.Start:
        case ElementType.SubProcessStartNode:
            return "Start";
        case ElementType.Return:
            return "Return";
        case ElementType.SequenceFlow:
            return "Sequence Flow";
        case ElementType.Task:
            return "Task";
        case ElementType.SubProcess:
            return "Scope";
        case ElementType.Throw:
            return "Throw";
        case ElementType.While:
            return "While";
        case ElementType.GlobalErrorHandler:
            return FRENDS.Localizations.ProcessErrorHandler;
        case ElementType.Dmn:
            return "DMN task";
        case ElementType.NativeAi:
            return "AI Connector";
        case ElementType.SharedState:
            return "Shared state task";
        case ElementType.DataObjectReference:
            return "Data Object reference";
        case ElementType.DataStoreReference:
            return "Data Store reference";
        case ElementType.HydrationPoint:
            return "Checkpoint";
        case ElementType.ScheduledRehydration:
            return "Scheduled resume";
        case ElementType.SignalRehydration:
            return "Signal resume";
        case ElementType.Vdm:
            return "Visual Data Mapper";
        default:
            return "";
    }
}

export function getElementDocumentationIdentifier(element: IElement): string {
    switch (element.type) {
        case ElementType.Expression: {
            const usingStatementMode = element.parameters[USE_STATEMENT_MODE]?.["value"]
            return usingStatementMode ? "Code Statement" : "Code";
        }
        default: {
            return getElementName(element.type);
        }
    }
}
